import {
  propertyTypes,
  conditionTypes,
  parkingTypes,
  petsAllowedTypes,
  tenantTypes,
  buildingFacilities,
} from "@luntero/db/contracts/attributes";
import type { AttributeKey } from "@luntero/db/contracts/attributes";
export {
  propertyTypes,
  type ListingType,
  conditionTypes,
  parkingTypes,
  petsAllowedTypes,
  tenantTypes,
  buildingFacilities,
  type AttributeKey,
  attributeKeys,
} from "@luntero/db/contracts/attributes";
import { z } from "zod";

type AttributeOptions =
  | {
      type: "options" | "options-multiple";
      options: readonly [string, string, ...string[]];
    }
  | {
      type: "unit";
      unit: string;
    }
  | {
      type: "amount";
    }
  | {
      type: "boolean";
    }
  | {
      type: "number";
    }
  | {
      type: "text";
    }
  | {
      type: "date";
    };

export const kitchenTypes = ["OPEN", "SEPARATE", "KITCHENETTE"] as const;

export const cooktopTypes = [
  "GAS",
  "INDUCTION",
  "CERAMIC",
  "ELECTRIC",
] as const;

export const energySources = [
  "GAS",
  "DISTRICT",
  "ALL_ELECTRIC",
  "MIXED",
  "UNKNOWN",
] as const;

export const applianceTypes = [
  "DISHWASHER",
  "OVEN",
  "MICROWAVE",
  "FRIDGE",
  "FREEZER",
  "WASHER",
  "DRYER",
] as const;

export const listingAttributeDefinition: Record<
  AttributeKey,
  AttributeOptions
> = {
  PRICE: {
    type: "unit",
    unit: "EUR",
  },
  ROOMS_COUNT: {
    type: "amount",
  },
  SIZE: {
    type: "unit",
    unit: "m²",
  },
  FURNISHING_TYPE: {
    type: "options",
    options: ["SHELL", "FURNISHED", "UPHOLSTERED"],
  },
  ENERGY_LABEL: {
    type: "text",
  },
  PROPERTY_TYPE: {
    type: "options",
    options: [...propertyTypes],
  },
  PLOT_AREA: {
    type: "unit",
    unit: "m²",
  },
  VOLUME: {
    type: "unit",
    unit: "m³",
  },
  BEDROOMS: {
    type: "amount",
  },
  BATHROOMS: {
    type: "amount",
  },
  HAS_GARDEN: {
    type: "boolean",
  },
  HAS_BALCONY: {
    type: "boolean",
  },
  HAS_TERRACE: {
    type: "boolean",
  },
  GARDEN_ORIENTATION: {
    type: "options",
    options: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
  },
  GARDEN_SIZE: {
    type: "unit",
    unit: "m²",
  },
  CONSTRUCTION_YEAR: {
    type: "number",
  },
  PARKING_TYPE: {
    type: "options",
    options: parkingTypes,
  },
  HAS_GARAGE: {
    type: "boolean",
  },
  GARAGE_SIZE: {
    type: "number", // number of cars
  },
  AMENITIES: {
    type: "options-multiple",
    options: [
      "RENEWABLE_ENERGY",
      "AIR_CONDITIONING",
      "ELEVATOR",
      "SWIMMING_POOL",
      "BOILER",
      "FIREPLACE",
      "BATHTUB",
      "SHOWER",
    ],
  },
  SERVICE_COSTS: {
    type: "unit",
    unit: "EUR",
  },
  PETS_ALLOWED: {
    type: "options",
    options: petsAllowedTypes,
  },
  CONTRACT_TYPE: {
    type: "options",
    options: ["A", "B", "C"],
  },
  MAX_NUMBER_OF_TENANTS: {
    type: "amount",
  },
  CONDITION: {
    type: "options",
    options: conditionTypes,
  },
  MIN_AGE: {
    type: "number", //inclusive
  },
  PREF_GENDER: {
    type: "options-multiple",
    options: ["MALE", "FEMALE", "NONBINARY"],
  },
  FLOOR_NUMBER: {
    type: "amount",
  },
  HEATING_TYPE: {
    type: "options",
    options: [
      "CENTRAL",
      "INDIVIDUAL",
      "NONE",
      "ELECTRIC",
      "SOLAR",
      "GAS",
      "FIREPLACE",
    ],
  },
  SMOKING_ALLOWED: {
    type: "boolean",
  },
  BUILDING_FACILITIES: {
    type: "options-multiple",
    options: buildingFacilities,
  },
  SHARED_FACILITIES: {
    type: "options-multiple",
    options: ["KITCHEN", "BATHROOM", "LIVING_ROOM", "LAUNDRY", "ROOFTOP"],
  },
  DEPOSIT_AMOUNT: {
    type: "unit",
    unit: "EUR",
  },
  GARDEN_DESCRIPTION: {
    type: "text",
  },
  TENANT_TYPE: {
    type: "options-multiple",
    options: tenantTypes,
  },
  MUSICAL_INSTRUMENTS: {
    type: "options",
    options: ["YES", "NO", "NEGOTIABLE"],
  },
  AVAILABLE_FROM: {
    type: "date",
  },
  AVAILABLE_UNTIL: {
    type: "date",
  },
  MIN_DURATION: {
    type: "amount",
  },
  NUMBER_OF_FLOORS: {
    type: "amount",
  },
  KITCHEN_TYPE: {
    type: "options",
    options: kitchenTypes,
  },
  COOKTOP_TYPE: {
    type: "options",
    options: cooktopTypes,
  },
  ENERGY_SOURCE: {
    type: "options",
    options: energySources,
  },
  APPLIANCES: {
    type: "options-multiple",
    options: applianceTypes,
  },
  RENOVATION_YEAR: {
    type: "number",
  },
  BIKE_STORAGE: {
    type: "boolean",
  },
  STORAGE_PRESENT: {
    type: "boolean",
  },
  STORAGE_SIZE: {
    type: "unit",
    unit: "m²",
  },
  WWS_POINTS: {
    type: "number",
  },
  IS_SOCIAL_HOUSING: {
    type: "boolean",
  },
  IS_BOAT: {
    type: "boolean",
  },
};
export const attributeSchema = z.object({
  PROPERTY_TYPE: z
    .enum(propertyTypes)
    .nullable()
    .catch(null)
    .describe("Property type"),
  PLOT_AREA: z.number().nullable().catch(null).describe("Plot size (m²)"),
  VOLUME: z.number().nullable().catch(null).describe("Volume (m³)"),
  BEDROOMS: z.number().nullable().catch(null).describe("Number of bedrooms"),
  BATHROOMS: z.number().nullable().catch(null).describe("Number of bathrooms"),
  HAS_GARDEN: z.boolean().nullable().catch(null).describe("Has a garden"),
  HAS_BALCONY: z.boolean().nullable().catch(null).describe("Has a balcony"),
  HAS_TERRACE: z.boolean().nullable().catch(null).describe("Has a terrace"),
  GARDEN_ORIENTATION: z
    .enum(["N", "NE", "E", "SE", "S", "SW", "W", "NW"])
    .nullable()
    .catch(null)
    .describe("Garden orientation"),
  GARDEN_SIZE: z.number().nullable().catch(null).describe("Garden size (m²)"),
  CONSTRUCTION_YEAR: z.number().nullable().catch(null).describe("Year built"),
  PARKING_TYPE: z
    .enum(parkingTypes)
    .nullable()
    .catch(null)
    .describe("Parking type"),
  HAS_GARAGE: z.boolean().nullable().catch(null).describe("Has a garage"),
  GARAGE_SIZE: z.number().nullable().catch(null).describe("Garage size"),
  AMENITIES: z
    .array(
      z.enum([
        "RENEWABLE_ENERGY",
        "AIR_CONDITIONING",
        "ELEVATOR",
        "SWIMMING_POOL",
        "BOILER",
        "FIREPLACE",
        "BATHTUB",
        "SHOWER",
      ])
    )
    .nullable()
    .catch(null)
    .describe("Available amenities"),
  SERVICE_COSTS: z
    .number()
    .nullable()
    .catch(null)
    .describe("Service costs (€)"),
  PETS_ALLOWED: z
    .enum(petsAllowedTypes)
    .nullable()
    .catch(null)
    .describe("Pets allowed"),
  CONTRACT_TYPE: z
    .enum(["A", "B", "C"])
    .nullable()
    .catch(null)
    .describe("Contract type"),
  MAX_NUMBER_OF_TENANTS: z
    .number()
    .nullable()
    .catch(null)
    .describe("Max tenants"),
  CONDITION: z
    .enum(conditionTypes)
    .nullable()
    .catch(null)
    .describe("Property condition"),
  MIN_AGE: z.number().nullable().catch(null).describe("Minimum tenant age"),
  PREF_GENDER: z
    .array(z.enum(["MALE", "FEMALE", "NONBINARY"]))
    .nullable()
    .catch(null)
    .describe("Preferred tenant gender"),
  FLOOR_NUMBER: z.number().nullable().catch(null).describe("Floor number"),
  HEATING_TYPE: z
    .enum([
      "CENTRAL",
      "INDIVIDUAL",
      "NONE",
      "ELECTRIC",
      "SOLAR",
      "GAS",
      "FIREPLACE",
    ])
    .nullable()
    .catch(null)
    .describe("Heating type"),
  SMOKING_ALLOWED: z
    .boolean()
    .nullable()
    .catch(null)
    .describe("Smoking allowed"),
  BUILDING_FACILITIES: z
    .array(z.enum(buildingFacilities))
    .nullable()
    .catch(null)
    .describe("Building facilities"),
  SHARED_FACILITIES: z
    .array(z.enum(["KITCHEN", "BATHROOM", "LIVING_ROOM", "LAUNDRY", "ROOFTOP"]))
    .nullable()
    .catch(null)
    .describe("Shared facilities"),
  DEPOSIT_AMOUNT: z
    .number()
    .nullable()
    .catch(null)
    .describe("Deposit amount (€)"),
  GARDEN_DESCRIPTION: z
    .string()
    .nullable()
    .catch(null)
    .describe("Garden description"),
  TENANT_TYPE: z
    .array(z.enum(tenantTypes))
    .nullable()
    .catch(null)
    .describe("Preferred tenants"),
  MUSICAL_INSTRUMENTS: z
    .enum(["YES", "NO", "NEGOTIABLE"])
    .nullable()
    .catch(null)
    .describe("Musical instruments allowed"),
  AVAILABLE_FROM: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .catch(null)
    .describe(
      "Available from (YYYY-MM-DD). If it is 'immediately', leave null"
    ),
  AVAILABLE_UNTIL: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .catch(null)
    .describe("Available until (YYYY-MM-DD)"),
  MIN_DURATION: z
    .number()
    .nullable()
    .catch(null)
    .describe("Min contract duration (months)"),
  NUMBER_OF_FLOORS: z
    .number()
    .nullable()
    .catch(null)
    .describe("Number of floors"),
  KITCHEN_TYPE: z
    .enum(kitchenTypes)
    .nullable()
    .catch(null)
    .describe("Layout of the kitchen"),
  COOKTOP_TYPE: z
    .enum(cooktopTypes)
    .nullable()
    .catch(null)
    .describe("Type of cooktop in the kitchen"),
  ENERGY_SOURCE: z
    .enum(energySources)
    .nullable()
    .catch(null)
    .describe("Main energy source for heating/cooking"),
  APPLIANCES: z
    .array(z.enum(applianceTypes))
    .nullable()
    .catch(null)
    .describe("Fixed appliances included with the property"),
  RENOVATION_YEAR: z
    .number()
    .nullable()
    .catch(null)
    .describe("Most recent year the property was renovated"),
  BIKE_STORAGE: z
    .boolean()
    .nullable()
    .catch(null)
    .describe("Property has a bike storage facility"),
  STORAGE_PRESENT: z
    .boolean()
    .nullable()
    .catch(null)
    .describe("Property has a separate storage room/box"),
  STORAGE_SIZE: z
    .number()
    .nullable()
    .catch(null)
    .describe("Size of the storage room (m²)"),
  WWS_POINTS: z
    .number()
    .nullable()
    .catch(null)
    .describe("Woningwaarderingsstelsel points for the property (if included)"),
  IS_SOCIAL_HOUSING: z
    .boolean()
    .nullable()
    .catch(null)
    .describe("Property falls under social housing rules"),
  IS_BOAT: z
    .boolean()
    .nullable()
    .catch(null)
    .describe("Property is a residential boat"),
});

export type ListingAttributeSchema = z.infer<typeof attributeSchema>;
